import React from "react";

import { useSidebarNav } from "#app/components/navigation/use-sidebar-nav";
import { useViewerIsShareOnly } from "#app/lib/hooks/use-permission";
import { useWorkflowsLanding } from "#app/lib/hooks/use-workflows-landing";
import {
  MODULE_HOTKEY_LEAD,
  SCREEN_HOTKEYS,
  TILES,
  resolveHotkeyRoute,
} from "#app/components/navigation/module-rail/tiles";
import { AnalyticsContext, NavigationEvent, trackEvent } from "#app/lib/analytics";
import { typingSomewhere } from "#app/lib/utils/typing-target";

/** How long the lead key stays armed before the chord lapses. */
const CHORD_WINDOW_MS = 1500;

/**
 * `G` then a letter navigates to a module — the chord the rail tooltips
 * advertise ("Go to Workflows", G then W) — or to a screen inside one that
 * has its own letter (`SCREEN_HOTKEYS`: G then I for the Inbox, G then S
 * for Settings).
 *
 * It resolves against the static registries, NOT the viewer's rail: a module
 * the viewer has hidden from the rail is still a place they can go, and hiding
 * a tile should not silently retire its shortcut. The one narrowing is the
 * viewer's tier — a Guest's chord to a member module lapses like any letter
 * that is not a chord, since the module is not theirs to reach by any door
 * (`tileVisibleToViewer`). Route resolution is the
 * rail's own (`resolveHotkeyRoute` → `resolveModuleRoute`), so `G W` lands on
 * the same workflow's board as pressing the tile does.
 */
export function useModuleHotkeys(): void {
  const { navigate } = useSidebarNav();
  const workflowId = useWorkflowsLanding();
  const viewerIsShareOnly = useViewerIsShareOnly();

  // Armed-at timestamp rather than a boolean, so the window is checked at the
  // second keystroke and no timer has to be cancelled on every unrelated key.
  const armedAt = React.useRef<number | null>(null);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (typingSomewhere(e.target)) {
        armedAt.current = null;
        return;
      }
      // `e.key` (not `e.code`) so the chord follows the viewer's layout — the
      // letter they see printed is the letter that works. No modifier is held,
      // so `e.key` is always the character here.
      const key = e.key.toUpperCase();

      const armed =
        armedAt.current != null &&
        performance.now() - armedAt.current < CHORD_WINDOW_MS;

      if (armed) {
        armedAt.current = null;
        const route = resolveHotkeyRoute(
          key,
          { workflowId },
          viewerIsShareOnly,
        );
        if (route == null) return; // Not a chord letter — it just lapses.
        e.preventDefault();
        const module =
          TILES.find((tile) => tile.hotkey === key)?.id ??
          SCREEN_HOTKEYS.find((screen) => screen.hotkey === key)?.id ??
          "unknown";
        trackEvent({
          context: AnalyticsContext.Navigation,
          event: NavigationEvent.ModuleOpen,
          data: { module, via: "hotkey" },
        });
        navigate(route);
        return;
      }

      if (key === MODULE_HOTKEY_LEAD) {
        // Do NOT preventDefault: on its own the lead key has done nothing yet,
        // and swallowing it would break a page that uses plain `g`.
        armedAt.current = performance.now();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [workflowId, viewerIsShareOnly, navigate]);
}
